import {
    useCallback,
    useId,
    useMemo,
    useState,
    type CSSProperties,
    type FC,
} from 'react'
import { Collapse, Group, Loader, Stack, Text, TextInput } from '@mantine/core'
import { IconCheck, IconChevronDown } from '@tabler/icons-react'
import { useHumanInTheLoop } from '@copilotkit/react-core/v2'
import { ToolCallStatus } from '@copilotkit/core'
import { z } from 'zod'
import {
    cardStyle,
    FONT,
    BRAND,
    BRAND_LIGHT,
    BRAND_HOVER,
    BORDER,
    SURFACE,
    TEXT,
    DIMMED,
    ON_PRIMARY,
} from './widgetStyles'
import { HITL_CANCELLED_RESULT } from './hitlCancel'
import { joinAnswer, parseAnswer, splitAnswer } from './choiceAnswer'

// The single, flexible "ask the user a question" tool. The backend agent fills
// `title` + `options` (and the flags) when it needs an answer. Mirrors the
// backend `ask_user_question` LongRunningFunctionTool signature.
const choiceSchema = z.object({
    title: z
        .string()
        .describe(
            'Your question / short heading (e.g. "How would you like to proceed?", "Refine your audience").'
        ),
    options: z
        .array(
            z.object({
                label: z
                    .string()
                    .describe(
                        'Short option label — a few words, no trailing punctuation.'
                    ),
                description: z
                    .string()
                    .optional()
                    .describe(
                        'Optional one-line description shown under the label. Keep it brief.'
                    ),
            })
        )
        .describe(
            '2 or more options (keep each scannable; ~8 is plenty). Each has a short `label` and an optional brief one-line `description`.'
        ),
    allow_multiple: z
        .boolean()
        .describe(
            'If True, let the user select more than one option — e.g. choosing several strategies to pursue at once.'
        ),
    // No `allow_other`: the user picks from `options` only. For an off-list
    // escape the agent adds an explicit option (e.g. "None of these"); to answer
    // freely the user types in the composer, dismissing the widget.
})

type ChoiceArgs = z.infer<typeof choiceSchema>
type ChoiceOption = ChoiceArgs['options'][number]

// Superset of the three render-prop shapes useHumanInTheLoop passes (one per
// ToolCallStatus). A component accepting this wider type is assignable to the
// hook's narrower per-status union, so we can narrow on `status` ourselves.
interface ChoicePromptProps {
    args: Partial<ChoiceArgs>
    status: ToolCallStatus
    result: string | undefined
    respond: ((result: unknown) => Promise<void>) | undefined
}

// Inline-style tokens (FONT, BRAND, …) and the card chrome (`cardStyle`) live
// in ./widgetStyles — see the note there on why chat widgets are styled inline.

interface OptionRowProps {
    label: string
    description?: string
    selected: boolean
    multiple: boolean
    interactive: boolean
    disabled?: boolean
    onSelect?: () => void
    // Shared radio-group name for the native input (single-select grouping).
    name?: string
}

// A selectable option row, styled entirely inline so it survives the chat's
// CSS. Label + an optional wrapping description. Renders as a <button> when
// interactive, otherwise a static <div> (locked / result state).
const OptionRow: FC<OptionRowProps> = ({
    label,
    description,
    selected,
    multiple,
    interactive,
    disabled = false,
    onSelect,
    name,
}) => {
    const [hovered, setHovered] = useState(false)
    const [focused, setFocused] = useState(false)
    const active = interactive && hovered && !disabled
    const rowStyle: CSSProperties = {
        // Containing block for the visually-hidden absolute <input> below, so it
        // scrolls/clips with its own row instead of escaping up to the nearest
        // positioned ancestor (the chat) and inflating its scroll height.
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        width: '100%',
        textAlign: 'left',
        padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)',
        margin: 0,
        borderRadius: 'var(--mantine-radius-md)',
        border: `1px solid ${selected || active ? BRAND : BORDER}`,
        background: selected ? BRAND_LIGHT : SURFACE,
        color: TEXT,
        fontFamily: FONT,
        cursor: interactive && !disabled ? 'pointer' : 'default',
        // Dim unselected options once the choice is locked.
        opacity:
            !interactive && !selected ? 0.55 : disabled && !selected ? 0.6 : 1,
        transition: 'border-color 120ms ease, background 120ms ease',
        // Keyboard focus ring — the real <input> is visually hidden, so the row
        // shows focus itself (:focus-within can't be expressed inline).
        outline: focused ? `2px solid ${BRAND}` : 'none',
        outlineOffset: 2,
    }
    const indicator: CSSProperties = {
        width: 20,
        height: 20,
        marginTop: 1,
        flexShrink: 0,
        borderRadius: multiple ? 5 : '50%',
        border: `2px solid ${selected ? BRAND : BORDER}`,
        background: selected ? BRAND : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }
    const inner = (
        <>
            <span style={indicator} aria-hidden>
                {selected && (
                    <IconCheck
                        size={14}
                        stroke={3}
                        style={{ color: ON_PRIMARY }}
                    />
                )}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
                <span
                    style={{
                        display: 'block',
                        fontSize: 'var(--mantine-font-size-md)',
                        fontWeight: 500,
                        lineHeight: 1.35,
                    }}
                >
                    {label}
                </span>
                {description && (
                    <span
                        style={{
                            display: 'block',
                            marginTop: 2,
                            fontSize: 'var(--mantine-font-size-sm)',
                            color: DIMMED,
                            lineHeight: 1.4,
                        }}
                    >
                        {description}
                    </span>
                )}
            </span>
        </>
    )
    if (!interactive) {
        return <div style={rowStyle}>{inner}</div>
    }
    // A <label> wrapping a visually-hidden NATIVE input: real radio/checkbox
    // semantics + keyboard for free, while the visible row stays fully inline-
    // styled (so it survives the chat bubble's CSS reset).
    return (
        <label
            style={rowStyle}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <input
                type={multiple ? 'checkbox' : 'radio'}
                name={name}
                aria-label={description ? `${label}, ${description}` : label}
                checked={selected}
                disabled={disabled}
                onChange={() => onSelect?.()}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                style={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    margin: -1,
                    padding: 0,
                    overflow: 'hidden',
                    clip: 'rect(0 0 0 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }}
            />
            {inner}
        </label>
    )
}

const Title: FC<{ children?: string }> = ({ children }) =>
    children ? (
        <Text
            fw={600}
            style={{
                fontFamily: FONT,
                fontSize: 'var(--mantine-font-size-lg)',
                lineHeight: 1.3,
            }}
        >
            {children}
        </Text>
    ) : null

// Shared chevron toggle used by the completed-state cards to reveal/hide the
// options the user didn't pick.
const toggleButtonStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: FONT,
    fontSize: 'var(--mantine-font-size-xs)',
    fontWeight: 600,
    color: BRAND,
}

const OptionsToggle: FC<{
    open: boolean
    onToggle: () => void
    children: string
}> = ({ open, onToggle, children }) => (
    <button type='button' onClick={onToggle} style={toggleButtonStyle}>
        <IconChevronDown
            size={12}
            style={{
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 120ms ease',
            }}
        />
        {children}
    </button>
)

// Compact, read-only rendering of the options hidden behind a toggle — shared
// by the dismissed and answered states so they look identical. More compact
// than the selectable OptionRow, but each option is a bold label with its
// description on the next line (and a gap between options) so it stays clear
// where one option ends and the next begins.
const CompactOptionList: FC<{ options: ChoiceOption[] }> = ({ options }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {options.map((opt) => (
            <div key={`${opt.label}:${opt.description ?? ''}`}>
                <div
                    style={{
                        fontFamily: FONT,
                        fontSize: 'var(--mantine-font-size-sm)',
                        fontWeight: 600,
                        lineHeight: 1.35,
                        color: TEXT,
                    }}
                >
                    {opt.label}
                </div>
                {opt.description && (
                    <div
                        style={{
                            fontFamily: FONT,
                            fontSize: 'var(--mantine-font-size-xs)',
                            lineHeight: 1.4,
                            color: DIMMED,
                        }}
                    >
                        {opt.description}
                    </div>
                )}
            </div>
        ))}
    </div>
)

// The user sent a message instead of answering — HitlGuardedInput resolved this
// call with the dismissal sentinel. Show a muted note, with a toggle to reveal
// the options that were offered (compact, read-only — not selectable).
const DismissedChoice: FC<{ args: Partial<ChoiceArgs> }> = ({ args }) => {
    const [open, setOpen] = useState(false)
    const options = args.options ?? []
    return (
        <div style={cardStyle}>
            <Stack gap='xs'>
                <Title>{args.title}</Title>
                <Text size='sm' style={{ fontFamily: FONT, color: DIMMED }}>
                    Dismissed — you asked something else.
                </Text>
                {options.length > 0 && (
                    <>
                        <OptionsToggle
                            open={open}
                            onToggle={() => setOpen((o) => !o)}
                        >
                            {open ? 'Hide options' : 'Show options'}
                        </OptionsToggle>
                        <Collapse in={open}>
                            <CompactOptionList options={options} />
                        </Collapse>
                    </>
                )}
            </Stack>
        </div>
    )
}

// Locked, read-only state after the user answered: show the selected option(s)
// with their normal selected styling, and collapse the options the user didn't
// pick behind a toggle.
const AnsweredChoice: FC<{
    args: Partial<ChoiceArgs>
    result: string | undefined
}> = ({ args, result }) => {
    const [showOthers, setShowOthers] = useState(false)
    const options = args.options ?? []
    const multiple = !!args.allow_multiple
    const selectedSet = new Set(splitAnswer(result))
    const selectedOptions = options.filter((o) => selectedSet.has(o.label))
    const otherOptions = options.filter((o) => !selectedSet.has(o.label))
    return (
        <div style={cardStyle}>
            <Stack gap='md'>
                <Title>{args.title}</Title>
                <Stack gap='sm'>
                    {selectedOptions.map((opt) => (
                        <OptionRow
                            key={`${opt.label}:${opt.description ?? ''}`}
                            label={opt.label}
                            description={opt.description}
                            multiple={multiple}
                            interactive={false}
                            selected
                        />
                    ))}
                    {otherOptions.length > 0 && (
                        <>
                            <OptionsToggle
                                open={showOthers}
                                onToggle={() => setShowOthers((o) => !o)}
                            >
                                {showOthers
                                    ? 'Hide other options'
                                    : 'Show other options'}
                            </OptionsToggle>
                            <Collapse in={showOthers}>
                                <CompactOptionList options={otherOptions} />
                            </Collapse>
                        </>
                    )}
                </Stack>
            </Stack>
        </div>
    )
}

const ChoicePrompt: FC<ChoicePromptProps> = ({
    args,
    status,
    respond,
    result,
}) => {
    // Dismissed (HitlGuardedInput cancelled this call when the user sent a
    // message instead of answering). `parseAnswer` unwraps the backend's
    // round-trip envelope (e.g. `{"success":true,"result":"<sentinel>",...}`)
    // so we match the recovered answer exactly, not the raw string.
    if (
        status === ToolCallStatus.Complete &&
        parseAnswer(result) === HITL_CANCELLED_RESULT
    ) {
        return <DismissedChoice args={args} />
    }

    // Answered, locked state — show the selection; collapse the rest.
    if (status === ToolCallStatus.Complete) {
        return <AnsweredChoice args={args} result={result} />
    }

    // Interactive state: the run is paused waiting on the user.
    if (status === ToolCallStatus.Executing && respond) {
        return <ChoicePromptForm args={args} respond={respond} />
    }

    // InProgress: args are still streaming in.
    return (
        <Group
            gap={8}
            wrap='nowrap'
            align='center'
            style={{ fontFamily: FONT }}
        >
            <Loader size={16} />
            <Text size='md' style={{ color: DIMMED }}>
                Preparing options...
            </Text>
        </Group>
    )
}

interface ChoicePromptFormProps {
    args: Partial<ChoiceArgs>
    respond: (result: unknown) => Promise<void>
}

const ChoicePromptForm: FC<ChoicePromptFormProps> = ({ args, respond }) => {
    // Memoized so it's a stable dependency for the callbacks below (a bare
    // `args.options ?? []` is a new array each render).
    const options: ChoiceOption[] = useMemo(
        () => args.options ?? [],
        [args.options]
    )
    const multiple = !!args.allow_multiple
    const hasOptions = options.length > 0
    // Single-select submits on click; multi-select needs Confirm. If the call
    // arrives with NO options (a malformed / optionless ask), the widget
    // degrades to a single free-text reply so the paused run can still be
    // unblocked. To answer off-list, the user types in the composer, which
    // dismisses this widget (see HitlGuardedInput).

    // Selection is tracked by option INDEX (not label) so two options sharing a
    // label don't co-select.
    const [selected, setSelected] = useState<number[]>([])
    const [otherText, setOtherText] = useState('')
    const [pending, setPending] = useState(false)
    const [confirmHovered, setConfirmHovered] = useState(false)
    // Stable shared name so the single-select radios form one native group.
    const radioGroup = useId()

    const computeAnswer = useCallback(
        (indices: number[]): string => {
            // No options → the answer is purely the free-text reply.
            if (!hasOptions) return otherText.trim()
            return joinAnswer(
                indices
                    .map((i) => options[i]?.label)
                    .filter((l): l is string => !!l)
            )
        },
        [hasOptions, options, otherText]
    )

    const submit = useCallback(
        (indices: number[]) => {
            if (pending) return
            const answer = computeAnswer(indices)
            if (!answer) return
            setPending(true)
            respond(answer).catch((error: unknown) => {
                // A rejected respond (network/runtime error) would otherwise
                // leave the widget permanently disabled; re-enable so the user
                // can retry. Success unmounts the widget, so we only reset on
                // failure.
                console.error('HITL respond failed:', error)
                setPending(false)
            })
        },
        [computeAnswer, pending, respond]
    )

    // Select a predefined option by index. Single-select submits the pick
    // immediately; multi-select toggles it into the set.
    const handleSelect = useCallback(
        (index: number) => {
            if (pending) return
            if (multiple) {
                setSelected((prev) =>
                    prev.includes(index)
                        ? prev.filter((i) => i !== index)
                        : [...prev, index]
                )
                return
            }
            setSelected([index])
            submit([index])
        },
        [multiple, pending, submit]
    )

    const canSubmit = computeAnswer(selected).length > 0

    const confirmStyle: CSSProperties = {
        padding: '10px 22px',
        borderRadius: 'var(--mantine-radius-md)',
        border: 'none',
        background:
            confirmHovered && canSubmit && !pending ? BRAND_HOVER : BRAND,
        color: ON_PRIMARY,
        fontFamily: FONT,
        fontSize: 'var(--mantine-font-size-md)',
        fontWeight: 600,
        cursor: !canSubmit || pending ? 'not-allowed' : 'pointer',
        opacity: !canSubmit || pending ? 0.5 : 1,
        transition: 'background 120ms ease',
    }

    return (
        <div style={cardStyle}>
            <Stack gap='md'>
                <Title>{args.title}</Title>

                {!hasOptions && !args.title && (
                    <Text size='sm' style={{ fontFamily: FONT, color: DIMMED }}>
                        Type your response to continue.
                    </Text>
                )}

                {hasOptions && (
                    <Stack gap='sm'>
                        {options.map((opt, i) => (
                            <OptionRow
                                key={`${opt.label}:${opt.description ?? ''}`}
                                label={opt.label}
                                description={opt.description}
                                multiple={multiple}
                                interactive
                                selected={selected.includes(i)}
                                disabled={pending}
                                onSelect={() => handleSelect(i)}
                                name={radioGroup}
                            />
                        ))}
                    </Stack>
                )}

                {!hasOptions && (
                    <TextInput
                        size='md'
                        placeholder='Type your answer'
                        value={otherText}
                        disabled={pending}
                        onChange={(e) => setOtherText(e.currentTarget.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') submit(selected)
                        }}
                        styles={{
                            input: {
                                fontFamily: FONT,
                                borderRadius: 'var(--mantine-radius-md)',
                            },
                        }}
                    />
                )}

                {(multiple || !hasOptions) && (
                    <Group justify='flex-end' mt={4}>
                        <button
                            type='button'
                            disabled={!canSubmit || pending}
                            style={confirmStyle}
                            onClick={() => submit(selected)}
                            onMouseEnter={() => setConfirmHovered(true)}
                            onMouseLeave={() => setConfirmHovered(false)}
                        >
                            {pending ? 'Sending...' : 'Confirm'}
                        </button>
                    </Group>
                )}
            </Stack>
        </div>
    )
}

// Registers the `ask_user_question` human-in-the-loop tool. Call once from a
// component inside the CopilotKit provider tree (ThreadPage).
export function useChoicePromptTool(): void {
    useHumanInTheLoop(
        {
            name: 'ask_user_question',
            description: `Ask the user to choose from a small set of options — the DEFAULT way to put any decision or confirmation to the user.

The \`title\` IS your question (e.g. "How would you like to proceed?"). Ask it EXACTLY ONCE, here — never also ask the question in prose before or after this call, and never restate the options as a text list. Doing either double-asks the user across turns.

Use this whenever the user needs to:
- pick from a few likely answers (a targeting approach, a dataset, a refinement),
- confirm an action — pass two options, e.g. "Yes, proceed" / "No, go back",
- choose a next step from a short list.

The user picks one option (or several when \`allow_multiple\` is set). If they need an off-list escape (e.g. "None of these"), add it as an explicit option. Always wait for the result before continuing.

Do NOT use it for open-ended questions with no small option set, or to present information that needs no answer — it pauses the run until the user responds.

Keep it scannable: put any explanation in each option's \`description\`, never in the \`title\` or the \`label\`.`,
            parameters: choiceSchema,
            render: ChoicePrompt,
        },
        []
    )
}
